import * as Sentry from '@sentry/react'
import {
  ERROR_VISIT_REPLAY_SHARE,
  ORDINARY_VISIT_REPLAY_SHARE,
  isReplayConsented,
} from './replayPolicy'

const REDACTED = '[REDACTED]'

// PII redaction patterns (security-baseline §2 item 10 + §9). Regex
// boundaries are slightly broader than the spec to catch loose input
// (e.g., phone numbers with separators); false positives over leaks.
const PII_PATTERNS: readonly RegExp[] = [
  /[\w.+-]+@[\w.-]+\.[\w-]{2,}/g,                                  // email
  /\+?\d[\d\s().-]{6,}\d/g,                                        // phone (loose, ≥7 digits)
  /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g,            // JWT
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, // UUID
]

// An admin console account reference (backend `admin/accountRefs.mjs`: 24 random bytes,
// base64url, 32 characters) is part of the PATH of the card, delete and restore doors, so the
// API client quotes it in `api_path`, Sentry quotes it in fetch breadcrumbs, and spans carry it
// in their names. A ref names an account only for the admin it was issued to, for an hour —
// and it still has no business in a report (security-baseline §9).
const ACCOUNT_REF_IN_PATH = /(\/admin\/accounts\/)[A-Za-z0-9_-]{32}(?![A-Za-z0-9_-])/g

/** `/admin/accounts/<ref>/delete` → `/admin/accounts/[REDACTED]/delete`. */
export function redactAccountRefs(input: string): string {
  return input.replace(ACCOUNT_REF_IN_PATH, `$1${REDACTED}`)
}

// Tags allowlist per baseline §9 Sentry section. Anything else is dropped.
const ALLOWED_TAGS = new Set([
  'api_method',
  'api_path',
  'api_status',
  'app_version',
  'route',
  'metric_id',
])

function scrubString(input: string): string {
  let out = redactAccountRefs(input)
  for (const pattern of PII_PATTERNS) out = out.replace(pattern, REDACTED)
  return out
}

function scrubValue(value: unknown): unknown {
  if (typeof value === 'string') return scrubString(value)
  if (Array.isArray(value)) return value.map(scrubValue)
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      out[key] = scrubValue(val)
    }
    return out
  }
  return value
}

export function scrubEvent(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.message && typeof event.message === 'string') {
    event.message = scrubString(event.message)
  }
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubString(ex.value)
    }
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      message: bc.message ? scrubString(bc.message) : bc.message,
      data: bc.data ? (scrubValue(bc.data) as Record<string, unknown>) : bc.data,
    }))
  }
  if (event.tags) {
    const filtered: Record<string, string> = {}
    for (const [key, raw] of Object.entries(event.tags)) {
      if (!ALLOWED_TAGS.has(key)) continue
      if (typeof raw !== 'string') continue
      filtered[key] = scrubString(raw)
    }
    event.tags = filtered
  }
  if (event.extra) {
    event.extra = scrubValue(event.extra) as Record<string, unknown>
  }
  if (event.user) {
    const id = typeof event.user.id === 'string' ? scrubString(event.user.id) : event.user.id
    const segment =
      typeof (event.user as { segment?: unknown }).segment === 'string'
        ? ((event.user as { segment?: string }).segment as string)
        : undefined
    event.user = {
      ...(id != null ? { id } : {}),
      ...(segment ? { segment } : {}),
    }
  }
  return event
}

type TransactionEvent = Parameters<NonNullable<Sentry.BrowserOptions['beforeSendTransaction']>>[0]

/**
 * Performance events never pass through `scrubEvent`. The one thing they are made to drop is
 * an account reference (`redactAccountRefs`), which rides in span names and request URLs.
 */
export function scrubTransaction(event: TransactionEvent): TransactionEvent {
  if (event.transaction) event.transaction = redactAccountRefs(event.transaction)
  for (const span of event.spans ?? []) {
    if (span.description) span.description = redactAccountRefs(span.description)
    for (const [key, value] of Object.entries(span.data ?? {})) {
      if (typeof value === 'string') span.data[key] = redactAccountRefs(value)
    }
  }
  return event
}

function normalized(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

export function getObservabilityConfig() {
  return {
    dsn: normalized(import.meta.env.VITE_SENTRY_DSN),
    environment: normalized(import.meta.env.VITE_ENV) ?? 'production',
    release: __APP_VERSION__,
  }
}

export function isObservabilityEnabled(): boolean {
  return Boolean(getObservabilityConfig().dsn)
}

// Safari has no requestIdleCallback; a short timeout is close enough.
function scheduleIdle(task: () => void) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(task, { timeout: 5000 })
  } else {
    window.setTimeout(task, 2000)
  }
}

export function initObservability() {
  const config = getObservabilityConfig()
  if (!config.dsn) return
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    tracesSampleRate: 0.1,
    // Both rates are read by the replay integration when it attaches, and
    // it attaches ONLY with consent (below) — so these decide how much is
    // recorded, never whether recording is allowed. See replayPolicy.ts.
    replaysSessionSampleRate: ORDINARY_VISIT_REPLAY_SHARE,
    replaysOnErrorSampleRate: ERROR_VISIT_REPLAY_SHARE,
    integrations: [Sentry.browserTracingIntegration()],
    beforeSend: scrubEvent,
    beforeSendTransaction: scrubTransaction,
  })
  enableReplayIfConsented()
}

let replayScheduled = false

/**
 * Attaches Session Replay — the camera — if, and only if, this visitor
 * accepted analytics. Called twice: once at startup for someone who
 * accepted on an earlier visit, and once by `ConsentBanner` the moment
 * Accept is pressed, so a fresh yes takes effect without a reload. There
 * is no path that attaches it otherwise; declining, or simply not
 * answering the banner, leaves the recorder unloaded — the ~38 KB chunk
 * is never even fetched.
 *
 * Error reporting is unaffected either way: a visitor who declines still
 * produces scrubbed error events, just no film to go with them.
 *
 * @returns whether the recorder was scheduled — false when observability
 *   is off, consent is missing, or it is already attached (idempotent).
 */
export function enableReplayIfConsented(): boolean {
  if (replayScheduled) return false
  if (!isObservabilityEnabled()) return false
  if (!isReplayConsented()) return false
  replayScheduled = true
  // Loaded on idle from a self-hosted lazy chunk (no Sentry-CDN origin;
  // keeps the pending strict-CSP decision D6.2 unconstrained).
  scheduleIdle(() => {
    // Replay is an enhancement — if its chunk fails to load (offline,
    // ad-blocker), error capture must keep working, so swallow.
    import('./sentryReplay').then(({ enableReplay }) => enableReplay()).catch(() => {})
  })
  return true
}

export function captureError(error: unknown, context?: Record<string, string>) {
  if (!isObservabilityEnabled()) return
  Sentry.withScope((scope) => {
    if (context) {
      for (const [key, value] of Object.entries(context)) {
        if (!ALLOWED_TAGS.has(key)) continue
        if (typeof value !== 'string') continue
        scope.setTag(key, scrubString(value))
      }
    }
    Sentry.captureException(error)
  })
}

export { Sentry }
